'use client';

import { MedicalDocParser } from '@/layers/presentation/components/patients/MedicalDocParser';
import MinimizedDocParser from '@/layers/presentation/components/patients/MinimizedDocParser';
import { DocumentParserProvider as DocumentParserContextProvider } from '@/layers/presentation/context/DocumentParserContext';
import { UserType } from '@/types/modelEnums';
import { usePathname } from 'next/navigation';
import React from 'react';
import { useGlobalState } from '../context/GlobalStateContext';

export interface DocumentParserProviderProps {
  children: React.ReactNode;
}

/**
 * Global provider for the document parser functionality.
 * This should be wrapped around the app in a high-level component to ensure
 * the parser state persists across page navigation and remains accessible
 * throughout the application.
 */
export const DocumentParserProvider: React.FC<DocumentParserProviderProps> = ({ children }) => {
  const pathname = usePathname();
  const { user } = useGlobalState();

  // Only render the global instance when we're NOT on the patient page
  // The patient page will render its own instance with the onApproveParams handler
  const isPatientPage = pathname === '/patient';

  return (
    <DocumentParserContextProvider>
      {/* The MedicalDocParser modal - only render the global instance when not on patient page */}
      {!isPatientPage && (
        <MedicalDocParser
          approveButtonText={
            user?.type === UserType.PATIENT ? 'Go to Patient Home' : 'Go to Patient page'
          }
        />
      )}

      {/* The minimized version of the parser, visible on all pages when minimized */}
      <MinimizedDocParser />

      {children}
    </DocumentParserContextProvider>
  );
};

export default DocumentParserProvider;
